import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useFormikContext } from 'formik';
import { ColumnDef } from '@tanstack/react-table';
import GridTable from './GridTable';
import Dropdown from './Dropdown';
import { CurrentRecruitPipeline } from '../models/currentRecruitPipeline.model';
import { ListFilterModel } from '../models/listFilter.model';
import { DropdownModel } from '../models/dropdown.model';
import { setListQueryFilters } from '../store/reducers/listFilterReducer';
import rightArrowIcon from '../assets/icons/rightArrow.svg';
import { route } from '../constants';
import { getCurrencyFormat } from '../functions/stringFunctions';
import { Stage } from '../enums/enum';

interface CurrentRecruitPipelineProps {
	recruitPipeLineData?: CurrentRecruitPipeline[];
	stageIds: DropdownModel[];
	handleStages: (val: DropdownModel) => void;
}

const CurrentRecruitingPipeLine: React.FC<CurrentRecruitPipelineProps> = ({
	recruitPipeLineData,
	stageIds,
	handleStages,
}) => {
	const { getFieldProps } = useFormikContext();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const getHeaderStyled = (
		header: string,
		suffix?: string,
		headerWidth?: number,
	) => (
		<div
			className={`font-medium text-rcrm-title  ${headerWidth ? `w-[${headerWidth}px]` : 'w-[165px]'}`}
		>
			{header}
			{suffix ? <small className="font-medium"> ({suffix})</small> : null}
		</div>
	);
	const getCellStyled = (cell: string, cellWidth?: number) => (
		<div
			className={`font-medium text-rcrm-primary-blue ${cellWidth ? `w-[${cellWidth}px]` : 'w-[165px]'}`}
		>
			{cell || 'NA'}
		</div>
	);
	const columns: ColumnDef<CurrentRecruitPipeline>[] = [
		{
			accessorKey: 'name',
			cell: (info) => getCellStyled(info.getValue() as string),
			header: () => getHeaderStyled('Name'),
			footer: (props) => props.column.id,
			enableSorting: false,
		},
		{
			accessorKey: 'state',
			cell: (info) => getCellStyled(info.getValue() as string, 100),
			header: () => getHeaderStyled('State', '', 100),
			footer: (props) => props.column.id,
			enableSorting: false,
		},
		{
			accessorKey: 'staff',
			cell: (info) => getCellStyled(info.getValue() as string, 100),
			header: () => getHeaderStyled('Staff', '', 100),
			footer: (props) => props.column.id,
			enableSorting: false,
		},
		{
			accessorKey: 'production',
			cell: (info) =>
				getCellStyled(
					getCurrencyFormat(info.getValue() as string) as string,
					200,
				),
			header: () => getHeaderStyled('Production', '', 200),
			footer: (props) => props.column.id,
			enableSorting: false,
		},
	];
	const getStage = (val: string) => {
		switch (val) {
			case Stage.offer:
				return (
					<div className="flex gap-x-2 text-rcrm-lime items-center">
						<span className="bg-rcrm-lime w-2 h-2 rounded-xl"></span>
						<p>{val}</p>
					</div>
				);
			case Stage.prospects:
				return (
					<div className="flex gap-x-2 text-rcrm-secondary-blue items-center">
						<span className="bg-rcrm-secondary-blue w-2 h-2 rounded-xl"></span>
						<p>{val}</p>
					</div>
				);
			case Stage.lead:
				return (
					<div className="flex gap-x-2 text-rcrm-primary-blue items-center">
						<span className="bg-rcrm-primary-blue w-2 h-2 rounded-xl"></span>
						<p>{val}</p>
					</div>
				);
			case Stage.dueDiligence:
				return (
					<div className="flex gap-x-2 text-rcrm-blue items-center">
						<span className="bg-rcrm-blue w-2 h-2 rounded-xl"></span>
						<p>{val}</p>
					</div>
				);
			case Stage.siteVisit:
				return (
					<div className="flex gap-x-2 text-rcrm-bluish-green items-center">
						<span className="bg-rcrm-bluish-green w-2 h-2 rounded-xl"></span>
						<p>{val}</p>
					</div>
				);
			default:
				return (
					<div className="flex gap-x-2  items-center">
						<span className=" w-2 h-2 rounded-xl"></span>
						<p>{'NA'}</p>
					</div>
				);
		}
	};

	const handleShowAll = () => {
		const filters: ListFilterModel = {
			stages: [getFieldProps('stageId').value],
			recruiting_managers: getFieldProps('recruiting_manager_ids').value,
		};
		dispatch(
			setListQueryFilters({
				...filters,
			}),
		);
		navigate(`/${route.recruitInformation}/${route.allRecruits}`);
	};

	const handleStageSelection = (val: DropdownModel) => {
		handleStages?.(val);
	};

	return (
		<div className="h-full flex flex-col">
			<div className="flex justify-between items-center">
				<div className="font-semibold">Current Recruiting PipeLine</div>
				<Dropdown
					list={stageIds}
					label=""
					customListItemComponent={(val) => getStage(val.value)}
					isRequired={false}
					name="stageId"
					labelClassName="min-w-[25vh]"
					onChangeSelection={handleStageSelection}
				/>
			</div>
			<div className=" mt-2 rounded-lg bg-rcrm-primary-bg p-3 h-[92%] gap-2 flex flex-col">
				<div
					className="h-[80vh]"
					style={{ maxWidth: '100% !important' }}
				>
					{stageIds?.length && (
						<GridTable
							columns={columns}
							rows={recruitPipeLineData || []}
							widthClass="w-full"
							isHeaderSticky={true}
						></GridTable>
					)}
				</div>
				<div
					className="text-rcrm-primary-blue font-semibold flex justify-center py-4 items-center hover:cursor-pointer hover:underline"
					onClick={handleShowAll}
				>
					<span> Show All</span>
					<img className="pl-2" src={rightArrowIcon}></img>
				</div>
			</div>
		</div>
	);
};

export default CurrentRecruitingPipeLine;
